import React, { useEffect, useMemo } from 'react';

import Spinner from '@components/Spinner';

import ProfileMainTabs from '../../ProfileMain/ProfileMainTabs';
import ProfileMainHelmet from '../../ProfileMain/ProfileMainHelmet';

import useCustomDomainProfile from '../../../hooks/useCustomDomainProfile';
import useDevice from '@hooks/useDevice';
import useContactForm from '../../../hooks/useContactForm';

import UserInfo from './UserInfo';
import ContactButton from './ContactButton';
import SlideshowSection from './SlideshowSection';
import useMarginTop from './useMarginTop';
import { CustomDomainProfileProps, Device, Link } from './types';

const CustomDomainProfile: React.FC<CustomDomainProfileProps> = ({
  staticContext,
}) => {
  const { user, loading, onOpenSlide, linkPreviewData } =
    useCustomDomainProfile(staticContext?.domain);
  const { device } = useDevice() as { device: Device };
  const isMobile = useMemo(() => device.mobile && !!device.mobile(), [device]);
  const isTablet = useMemo(() => device.tablet && !!device.tablet(), [device]);

  const slideshow = useMemo(() => {
    return user?.links?.filter(({ type }: Link) => type === 'slide');
  }, [user?.links]);

  const { onOpenContactModal } = useContactForm(
    user?.contactDetails?.infoMessage,
    user?._id,
  );

  const { getMarginTop } = useMarginTop(isMobile, user);

  useEffect(() => {
    const uiWrapper = document && document.getElementById('react-ui-wrapper');

    if (uiWrapper && !isMobile && !isTablet) {
      uiWrapper.style.overflow = 'auto';
      uiWrapper.style.width = 'unset';
      uiWrapper.style.height = 'unset';
    }
  }, [isTablet, isMobile]);

  const marginTop = useMemo(() => {
    const isCentered =
      user?.backgroundSettings?.avatarAlignmentType === 'center';
    return isCentered ? getMarginTop('center') : getMarginTop();
  }, [user?.backgroundSettings?.avatarAlignmentType, getMarginTop]);

  return useMemo(
    () => (
      <>
        {loading ? (
          <div className="fixed inset-0 z-[1] flex h-full w-full items-center justify-center bg-black/70">
            <Spinner />
          </div>
        ) : null}
        {user && !loading ? (
          <div className="relative mx-auto grid h-auto w-full max-w-[1390px] grid-cols-[100%] pt-5 max-[1279px]:w-full max-[1279px]:justify-items-center max-[1279px]:p-0 max-[1279px]:pt-0 max-[743px]:mt-0 max-[743px]:gap-0">
            <UserInfo user={user} marginTop={marginTop} />
            <ContactButton
              user={user}
              onOpenContactModal={onOpenContactModal}
            />
            <SlideshowSection
              user={user}
              slideshow={slideshow || []}
              onOpenSlide={onOpenSlide}
            />
            <ProfileMainTabs
              userBlockHeight={140}
              userId={user?._id}
              username={user?.username}
              auths={user?.auths}
              isMe={false}
              isPreview={false}
            />
          </div>
        ) : null}
        {linkPreviewData ? (
          <ProfileMainHelmet
            domain={staticContext?.domain}
            linkPreviewData={linkPreviewData}
          />
        ) : null}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      linkPreviewData,
      onOpenSlide,
      user,
      loading,
      slideshow,
      marginTop,
      staticContext?.domain,
    ],
  );
};

export default CustomDomainProfile;
